import SingleSelectComponent from "../../components/single-select/single-select.component";
import { transStatus } from "../../config/global-variables";
import IncentiveCalculationModel from "../../models/incentive-calculation/incentive-calculation.model";
import ViewBase from "../view-base.view";

/**
 * Incentive Calculation
 *
 * FFIS payout calculation for a scope: pass the FR-03 sanity gate, then run it.
 *
 * ============================================================================================
 * THE PAGE IS ORDERED AS A SEQUENCE BECAUSE THE GATE IS A SEQUENCE
 * ============================================================================================
 * PRD FR-03: after ingestion the system shows target-vs-actual per segment, and CALCULATION
 * STAYS LOCKED UNTIL AN OPERATOR CONFIRMS IT. That is not a warning banner, it is a lock -
 * IncentiveCalcRunExecute refuses a payout run for an unconfirmed scope.
 *
 * So the three steps are laid out in order and each one is disabled until the previous is done,
 * rather than offering four buttons and letting the server refuse. An operator who can click
 * "Run" and be told no has learned nothing about why.
 *
 * ⚠️ BUILD AND CONFIRM ARE DELIBERATELY TWO CLICKS, WITH THE VARIANCE ROWS IN BETWEEN. Merging
 * them would be less typing and would defeat the point: the operator has to be able to READ the
 * numbers they are accepting. 21-sp-calc-run.sql puts it well - "a gate that merely displays a
 * warning gets clicked past".
 *
 * ⚠️ WM021103 AND WM021104 MUST NOT BE GRANTED TO THE SAME ROLE. This page runs the payout;
 * WM021104 approves it. Nothing here can prevent one person doing both - it is a grant decision.
 *
 * @author FFIS
 */
export default function IncentiveCalculationView() {
  ViewBase.call(this);

  const PRODUCT_GROUPS = [
    { key: 'MX', value: 'MX' },
    { key: 'AV', value: 'AV' },
  ];

  const PERIOD_TYPES = [
    { key: 'MONTHLY', value: 'MONTHLY' },
    { key: 'WEEKLY', value: 'WEEKLY' },
  ];

  /*
   * ⚠️ BATCH_NIGHTLY IS DELIBERATELY ABSENT.
   *
   * It belongs to the unattended nightly job and is EXEMPT from the FR-03 gate, so the batch can
   * show promoters a provisional figure without a human confirming inputs every night. A run
   * marked with it CANNOT BE APPROVED - IncentiveApprovalSubmit refuses it outright.
   *
   * Offering it here would let an operator produce something that looks like a payout, skipped
   * the gate, and then cannot be paid, with nothing on screen explaining why. The two values
   * below are the only ones an operator should ever choose.
   */
  const RUN_TYPES = [
    { key: 'MANUAL_FULL', value: 'MANUAL_FULL' },
    { key: 'BATCH_PERIOD', value: 'BATCH_PERIOD' },
  ];

  const DEFAULT_SCOPE = {
    productGroupCode: 'MX',
    periodType: 'MONTHLY',
    periodCode: '',
  };

  let scope = structuredClone(DEFAULT_SCOPE);

  let gateRows = [];
  /** Has the operator actually loaded the gate in this session? Confirm stays off until so. */
  let gateLoaded = false;
  let acknowledgeFail = false;
  let confirmComment = '';

  let runType = 'MANUAL_FULL';
  let runs = [];
  let detail = null;
  let selectedRunId = null;

  /* ---- derived gate state ------------------------------------------------ */

  const gateSegments = () => gateRows.length;
  const gateConfirmed = () => gateRows.filter(r => r.confirmed).length;
  const gateFailRows = () => gateRows.filter(r => r.varianceStatus === 'FAIL');

  /** The same predicate IncentiveCalcRunExecute applies: every segment, or the scope is locked. */
  const isGateOpen = () => gateSegments() > 0 && gateConfirmed() === gateSegments();

  const scopeIsComplete = () => !!(scope.productGroupCode && scope.periodType && scope.periodCode);

  /* ---- loading ----------------------------------------------------------- */

  const readEnvelope = (result) => (Array.isArray(result) ? result[0] : result?.transResult?.[0]);

  const loadGate = (quiet) => {
    if (!scopeIsComplete()) {
      return;
    }
    this.showLoading();
    IncentiveCalculationModel.getSanityCheck(scope)
      .then((result) => {
        gateRows = result || [];
        gateLoaded = true;
        acknowledgeFail = false;
      })
      .catch((error) => {
        if (!quiet) {
          this.handleError(error);
        }
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const searchRuns = () => {
    this.showLoading();
    IncentiveCalculationModel.searchRuns({
      productGroupCode: scope.productGroupCode,
      periodType: scope.periodType,
      periodCode: scope.periodCode,
    })
      .then((result) => {
        runs = result || [];
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const loadDetail = (calcRunId) => {
    this.showLoading();
    IncentiveCalculationModel.getRunDetail(calcRunId)
      .then((result) => {
        /* Three result sets: header, per-position summary, exceptions. */
        detail = {
          header: result?.[0]?.[0] || result?.header?.[0] || null,
          positions: result?.[1] || result?.positions || [],
          exceptions: result?.[2] || result?.exceptions || [],
        };
        selectedRunId = calcRunId;
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  /* ---- step 2: the gate -------------------------------------------------- */

  const onBuild = () => {
    /* Rebuilding discards an existing confirmation, so say so before doing it rather than
       after. An operator who confirmed yesterday and rebuilds today has to review again. */
    const warn = gateConfirmed() > 0
      ? this.getText('EM00000267',
          'This scope is already confirmed. Rebuilding DISCARDS that confirmation - the '
        + 'figures will have to be reviewed and confirmed again before a payout can be '
        + 'calculated. Continue?')
      : this.getText('EM00000268',
          'Build the sanity check for this scope from the current uploaded data?');

    this.showConfirm(warn, this.getText('LT00000022', 'Save'), (e) => {
      if (e !== 'YES') {
        return;
      }
      this.showLoading();
      IncentiveCalculationModel.buildSanityCheck(scope)
        .then((result) => {
          const trans = readEnvelope(result);
          this.showMessage(
            this.getText(trans?.code, trans?.message),
            trans?.transStatus,
            () => { loadGate(false); });
        })
        .catch((error) => { this.handleError(error); })
        .finally(() => { this.hideLoading(); });
    });
  };

  const onConfirm = () => {
    const fails = gateFailRows();

    if (fails.length && !acknowledgeFail) {
      /* Not a silent refusal: name the segments, because "acknowledge" has to mean the operator
         knows what they are overriding. FAIL means a MISSING INPUT - sellout with no target, or
         a target with no sellout - not underperformance. */
      this.showMessage(
        this.getText('EM00000269',
          'This scope has segment(s) with a missing input: '
        + fails.map(r => r.segment1 || '(unmapped)').join(', ')
        + '. FAIL here means a target or sellout figure is absent, not that anyone '
        + 'underperformed. Tick "Accept the failing segments" to confirm anyway - it is '
        + 'recorded against your name with your comment.'),
        transStatus.failed);
      return;
    }

    this.showConfirm(
      this.getText('EM00000270',
        'Confirming records that YOU accept these target and sellout figures as the basis for '
      + 'a payout. It unlocks calculation for this scope. Continue?'),
      this.getText('LT00000022', 'Save'),
      (e) => {
        if (e !== 'YES') {
          return;
        }
        this.showLoading();
        IncentiveCalculationModel.confirmSanityCheck({
          productGroupCode: scope.productGroupCode,
          periodType: scope.periodType,
          periodCode: scope.periodCode,
          comment: confirmComment,
          acknowledgeFail: acknowledgeFail,
        })
          .then((result) => {
            const trans = readEnvelope(result);
            this.showMessage(
              this.getText(trans?.code, trans?.message),
              trans?.transStatus,
              () => { loadGate(false); });
          })
          .catch((error) => { this.handleError(error); })
          .finally(() => { this.hideLoading(); });
      });
  };

  /* ---- step 3: run ------------------------------------------------------- */

  const runWith = (isSimulation) => {
    const body = {
      productGroupCode: scope.productGroupCode,
      periodType: scope.periodType,
      periodCode: scope.periodCode,
      runType: isSimulation ? 'SIMULATION' : runType,
    };

    const prompt = isSimulation
      ? this.getText('EM00000271',
          'Run a SIMULATION for this scope? It is marked as a simulation, no promoter ever '
        + 'sees it, and it cannot be approved.')
      : this.getText('EM00000272',
          'Run a REAL calculation for this scope. It writes payout figures that promoters and '
        + 'reports will read. Continue?');

    this.showConfirm(prompt, this.getText('LT00000022', 'Save'), (e) => {
      if (e !== 'YES') {
        return;
      }
      this.showLoading();
      const call = isSimulation
        ? IncentiveCalculationModel.simulate(body)
        : IncentiveCalculationModel.runCalculation(body);

      call
        .then((result) => {
          const trans = readEnvelope(result);
          this.showMessage(
            this.getText(trans?.code, trans?.message),
            trans?.transStatus,
            () => {
              searchRuns();
              const newId = Number(trans?.id);
              if (trans?.transStatus === transStatus.success && newId) {
                loadDetail(newId);
              }
            });
        })
        .catch((error) => { this.handleError(error); })
        .finally(() => { this.hideLoading(); });
    });
  };

  /* ---- render helpers ---------------------------------------------------- */

  const num = (v, dp) => (v === null || v === undefined
    ? '-'
    : Number(v).toLocaleString(undefined, {
        minimumFractionDigits: dp || 0, maximumFractionDigits: dp || 0 }));

  const pct = (v) => (v === null || v === undefined ? '-' : (Number(v) * 100).toFixed(1) + '%');

  const varianceBadge = (status) => {
    const cls = status === 'FAIL' ? 'bg-danger'
      : status === 'WARN' ? 'bg-warning text-dark' : 'bg-success';
    return m('span.badge', { class: cls }, status || '-');
  };

  /**
   * A run's authority, not just its status. Since nothing records which run is authoritative
   * until an approval is signed, an operator needs to see at a glance that a BATCH_NIGHTLY row
   * is a provisional figure from the unattended job and not something they asked for.
   */
  const runAuthorityBadge = (row) => {
    if (row.isSimulation) {
      return m('span.badge.bg-secondary', this.getText('LT00000260', 'SIMULATION'));
    }
    if (row.runType === 'BATCH_NIGHTLY') {
      return m('span.badge.bg-info.text-dark', this.getText('LT00000261', 'PROVISIONAL'));
    }
    return m('span.badge.bg-primary', this.getText('LT00000262', 'PAYOUT'));
  };

  const gateStatusLine = () => {
    if (!gateLoaded) {
      return m('.alert.alert-secondary.mb-0',
        this.getText('EM00000273',
          'Pick a scope and load the sanity check to see whether calculation is unlocked.'));
    }
    if (gateSegments() === 0) {
      return m('.alert.alert-warning.mb-0',
        this.getText('EM00000274',
          'LOCKED - nothing has been built for this scope yet. Build it, review the segments, '
        + 'then confirm.'));
    }
    if (!isGateOpen()) {
      return m('.alert.alert-warning.mb-0',
        this.getText('EM00000275', 'LOCKED - ')
        + (gateSegments() - gateConfirmed()) + ' of ' + gateSegments()
        + this.getText('EM00000276',
            ' segment(s) are not confirmed. A payout calculation will be refused.'));
    }
    return m('.alert.alert-success.mb-0',
      this.getText('EM00000277',
        'UNLOCKED - all segments confirmed. A payout calculation will run for this scope.'));
  };

  /* ---- sections ---------------------------------------------------------- */

  const scopeSection = () => m('.card.mb-3',
    m('.card-header.fw-semibold', this.getText('LT00000263', '1. Scope')),
    m('.card-body',
      m('.row.g-4',
        m('.col-md-3.position-relative',
          m('label.form-label[for=calcProductGroup]',
            this.getText('LT00000008', 'Product Group')),
          m(SingleSelectComponent, {
            id: 'calcProductGroup',
            items: PRODUCT_GROUPS,
            selectedItem: scope.productGroupCode,
            checkmark: true,
            keyOnly: true,
            onSelect: (item) => {
              scope.productGroupCode = item;
              gateLoaded = false;
              gateRows = [];
            },
          })),

        m('.col-md-3.position-relative',
          m('label.form-label[for=calcPeriodType]',
            this.getText('LT00000264', 'Period Type')),
          m(SingleSelectComponent, {
            id: 'calcPeriodType',
            items: PERIOD_TYPES,
            selectedItem: scope.periodType,
            checkmark: true,
            keyOnly: true,
            onSelect: (item) => {
              scope.periodType = item;
              gateLoaded = false;
              gateRows = [];
            },
          })),

        m('.col-md-3',
          m('label.form-label', this.getText('LT00000265', 'Period Code')),
          /* Free text on purpose. Fiscal weeks are NAMED, not numbered sequentially - W9B, W10,
             W11, W12, W13, W14A - and a month is yyyyMM. A generated dropdown would have to
             mirror vwIncentiveFiscalPeriod's split-week logic to be right. */
          m('input.form-control.form-control-bg', {
            value: scope.periodCode,
            placeholder: '202603 / W10',
            oninput: (e) => {
              scope.periodCode = e.target.value;
              gateLoaded = false;
              gateRows = [];
            },
          })),

        m('.col-md-3.d-flex.align-items-end.gap-2',
          m('button.btn.btn-outline-primary', {
            disabled: !scopeIsComplete(),
            onclick: () => { loadGate(false); searchRuns(); },
          }, this.getText('LT00000021', 'Search'))))));

  const gateSection = () => m('.card.mb-3',
    m('.card-header.fw-semibold.d-flex.justify-content-between.align-items-center',
      m('span', this.getText('LT00000266', '2. Sanity check (FR-03)')),
      m('.d-flex.gap-2',
        m('button.btn.btn-sm.btn-outline-secondary', {
          disabled: !scopeIsComplete(),
          onclick: onBuild,
        }, gateConfirmed() > 0
            ? this.getText('LT00000267', 'Rebuild')
            : this.getText('LT00000268', 'Build')),
        m('button.btn.btn-sm.btn-primary', {
          /* Confirm needs the rows on screen first - that is the whole point of the gate. */
          disabled: !gateLoaded || gateSegments() === 0 || isGateOpen(),
          onclick: onConfirm,
        }, this.getText('LT00000269', 'Confirm')))),

    m('.card-body',
      gateStatusLine(),

      gateRows.length > 0 && m('.table-responsive.mt-3',
        m('table.table.table-sm.table-hover',
          m('thead',
            m('tr',
              m('th', this.getText('LT00000270', 'Segment')),
              m('th.text-end', this.getText('LT00000271', 'Target Qty')),
              m('th.text-end', this.getText('LT00000272', 'Sellout Qty')),
              m('th.text-end', this.getText('LT00000273', 'AR Qty')),
              m('th.text-end', this.getText('LT00000274', 'Target Amt')),
              m('th.text-end', this.getText('LT00000275', 'Sellout Amt')),
              m('th.text-end', this.getText('LT00000276', 'AR Amt')),
              m('th', this.getText('LT00000277', 'Variance')),
              m('th', this.getText('LT00000278', 'Confirmed')),
              m('th', this.getText('LT00000279', 'By')))),
          m('tbody',
            gateRows.map(row => m('tr',
              m('td', row.segment1 || m('em.text-muted', '(unmapped)')),
              m('td.text-end', num(row.targetQty)),
              m('td.text-end', num(row.selloutQty)),
              m('td.text-end', pct(row.achievementRateQty)),
              m('td.text-end', num(row.targetAmt)),
              m('td.text-end', num(row.selloutAmt)),
              m('td.text-end', pct(row.achievementRateAmt)),
              m('td', varianceBadge(row.varianceStatus)),
              m('td', row.confirmed
                  ? m('i.bi.bi-check-circle-fill.text-success')
                  : m('i.bi.bi-dash-circle.text-muted')),
              m('td.small.text-muted', row.confirmedBy || '-')))))),

      /* The FAIL override, shown only when there is something to override. */
      gateFailRows().length > 0 && !isGateOpen() && m('.mt-3',
        m('.alert.alert-danger',
          m('div.fw-semibold', this.getText('LT00000280',
            'Segment(s) with a missing input')),
          m('div.small.mb-2', this.getText('EM00000278',
            'FAIL means a target or sellout figure is ABSENT for the segment - not that anyone '
          + 'underperformed. 60% of target comes back WARN and calculates normally. Check the '
          + 'upload before overriding.')),
          m('.form-check',
            m('input.form-check-input[type=checkbox]#ackFail', {
              checked: acknowledgeFail,
              onchange: (e) => { acknowledgeFail = e.target.checked; },
            }),
            m('label.form-check-label[for=ackFail]', this.getText('LT00000281',
              'Accept the failing segments - recorded against my name'))))),

      !isGateOpen() && gateSegments() > 0 && m('.mt-3',
        m('label.form-label', this.getText('LT00000282', 'Confirmation comment')),
        m('input.form-control.form-control-bg', {
          value: confirmComment,
          placeholder: this.getText('LT00000283',
            'What you checked, e.g. "targets reloaded 15 Mar, variances reviewed with Ops"'),
          oninput: (e) => { confirmComment = e.target.value; },
        }))));

  const runSection = () => m('.card.mb-3',
    m('.card-header.fw-semibold', this.getText('LT00000284', '3. Run')),
    m('.card-body',
      m('.row.g-4.align-items-end',
        m('.col-md-3.position-relative',
          m('label.form-label[for=calcRunType]', this.getText('LT00000285', 'Run Type')),
          m(SingleSelectComponent, {
            id: 'calcRunType',
            items: RUN_TYPES,
            selectedItem: runType,
            checkmark: true,
            keyOnly: true,
            onSelect: (item) => { runType = item; },
          })),

        m('.col-md-9.d-flex.gap-2',
          m('button.btn.btn-primary', {
            /* Disabled with the reason on screen above, rather than letting the server refuse.
               The stored procedure refuses too - this is not the only guard. */
            disabled: !scopeIsComplete() || !isGateOpen(),
            onclick: () => { runWith(false); },
          }, this.getText('LT00000286', 'Run calculation')),

          /* Simulation is exempt from the gate on purpose: it is how an operator investigates
             the very discrepancy the gate is reporting, so it stays enabled while locked. */
          m('button.btn.btn-outline-secondary', {
            disabled: !scopeIsComplete(),
            onclick: () => { runWith(true); },
          }, this.getText('LT00000287', 'Simulate')))),

      !isGateOpen() && m('.small.text-muted.mt-2', this.getText('EM00000279',
        'Calculation is locked until every segment above is confirmed. Simulation stays '
      + 'available - it is marked as a simulation, no promoter reads it, and it cannot be '
      + 'approved.'))));

  const runsSection = () => m('.card.mb-3',
    m('.card-header.fw-semibold', this.getText('LT00000288', 'Runs for this scope')),
    m('.card-body.p-0',
      runs.length === 0
        ? m('.p-3.text-muted', this.getText('LT00000289', 'No runs yet for this scope.'))
        : m('.table-responsive',
            m('table.table.table-sm.table-hover.mb-0',
              m('thead',
                m('tr',
                  m('th', this.getText('LT00000290', 'Run')),
                  m('th', this.getText('LT00000291', 'Authority')),
                  m('th', this.getText('LT00000292', 'Type')),
                  m('th', this.getText('LT00000293', 'Status')),
                  m('th.text-end', this.getText('LT00000294', 'Employees')),
                  m('th.text-end', this.getText('LT00000295', 'Failed')),
                  m('th.text-end', this.getText('LT00000296', 'Total')),
                  m('th', this.getText('LT00000297', 'Started')))),
              m('tbody',
                runs.map(row => m('tr.cursor-pointer', {
                  class: selectedRunId === row.calcRunId ? 'table-active' : '',
                  onclick: () => { loadDetail(row.calcRunId); },
                },
                  m('td', row.calcRunId),
                  m('td', runAuthorityBadge(row)),
                  m('td.small', row.runType),
                  m('td.small', row.statusCode),
                  m('td.text-end', num(row.rowsSucceeded)),
                  m('td.text-end',
                    row.rowsFailed > 0
                      ? m('span.text-danger.fw-semibold', num(row.rowsFailed))
                      : num(row.rowsFailed)),
                  m('td.text-end', num(row.totalFinalAmount, 2)),
                  m('td.small.text-muted', row.startDateTime))))))));

  /*
   * Split into three shallow helpers rather than one deeply nested return.
   *
   * The first version was a single expression closing with eleven consecutive parentheses, and
   * it had one too many - a syntax error that node's parser reported at a column in the middle
   * of that run, which says nothing about which of the nine nested m() calls was wrong. Mithril
   * markup nests fast; naming the pieces makes the nesting checkable by reading it.
   */
  const positionSummaryTable = () => {
    if (!detail || !detail.positions.length) {
      return null;
    }

    const head = m('thead', m('tr', [
      m('th', this.getText('LT00000299', 'Position')),
      m('th.text-end', this.getText('LT00000294', 'Employees')),
      m('th.text-end', this.getText('LT00000300', 'Capped')),
      m('th.text-end', this.getText('LT00000301', 'Disqualified')),
      m('th.text-end', this.getText('LT00000302', 'Zero pay')),
      m('th.text-end', this.getText('LT00000296', 'Total')),
    ]));

    const rows = detail.positions.map(row => m('tr', [
      m('td', row.positionCode),
      m('td.text-end', num(row.employees)),
      m('td.text-end', num(row.cappedCount)),
      m('td.text-end', num(row.disqualifiedCount)),
      m('td.text-end', num(row.zeroPayCount)),
      m('td.text-end', num(row.totalFinalAmount, 2)),
    ]));

    return m('.table-responsive.mb-4',
      m('table.table.table-sm', [head, m('tbody', rows)]));
  };

  /*
   * The exception list is the reason to open a run at all. A run can report SUCCESS with
   * hundreds of people at zero, and the reason code is what separates "sold nothing" from "the
   * segment mapping is broken" - NO_SELLOUT_IN_PERIOD and SEGMENT_UNMAPPED both pay nothing and
   * send you to completely different places.
   *
   * ⚠️ A zero with NO reason is rendered as a defect, not as a blank. IncentiveCalcRunDetailGet
   * flags exactly that case, and it has already caught a real one: a promoter with a result row
   * and no explanation for their zero.
   */
  const exceptionTable = () => {
    if (!detail) {
      return null;
    }
    if (!detail.exceptions.length) {
      return m('.text-muted.small', this.getText('LT00000304',
        'No exceptions - every person in scope produced a figure with a reason.'));
    }

    const head = m('thead', m('tr', [
      m('th', this.getText('LT00000305', 'Employee')),
      m('th', this.getText('LT00000299', 'Position')),
      m('th', this.getText('LT00000306', 'Site')),
      m('th.text-end', this.getText('LT00000307', 'Amount')),
      m('th', this.getText('LT00000308', 'Reason')),
    ]));

    const reasonCell = (row) => {
      if (row.disqualReasonCode) {
        return m('span.badge.bg-warning.text-dark', row.disqualReasonCode);
      }
      return m('em.text-danger',
        this.getText('LT00000309', 'zero with no reason - investigate'));
    };

    const rows = detail.exceptions.map(row => m('tr', [
      m('td', row.userID || row.userId),
      m('td.small', row.positionCode),
      m('td.small', row.siteCode),
      m('td.text-end', num(row.finalIncentiveAmt, 2)),
      m('td.small', reasonCell(row)),
    ]));

    return m('.table-responsive',
      m('table.table.table-sm.table-hover', [head, m('tbody', rows)]));
  };

  const detailSection = () => {
    if (!detail) {
      return null;
    }

    return m('.card.mb-3', [
      m('.card-header.fw-semibold',
        this.getText('LT00000298', 'Run detail') + ' - ' + (selectedRunId || '')),
      m('.card-body', [
        positionSummaryTable(),
        m('div.fw-semibold.mb-2', this.getText('LT00000303', 'Exceptions')),
        exceptionTable(),
      ]),
    ]);
  };

  /* ---- view -------------------------------------------------------------- */

  return {
    oninit: () => {
      /* Nothing is loaded until a scope is chosen. A blank period code would make the gate read
         return every segment of every period. */
    },

    view: () => {
      return [
        scopeSection(),
        gateSection(),
        runSection(),
        runsSection(),
        detailSection(),
        this.getBaseViews(),
      ];
    },
  };
}
